import { JumpTarget, useJump } from '@/components/MagicJump';
import ClientOnly from '@/components/Responsive/ClientOnly';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import logger from '@/utils/client-logger';
import { getSpmPositionCode, useGlobalSpmTerminal } from '@/components/Layout/spmTerminal';
import { getRouteCnName } from '@/utils/route-metadata';
import { setCampaignEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { HeaderProps } from '..';
import { PcTopBarCountDown, WapTopBarCountDown } from './CountDown';
import styles from './DingTong.module.css';
import {
  getCountdownDisplay,
  getDingTongBackgroundColor,
  getDingTongJumpContent,
  getDingTongTrackingJumpType,
  getNextDingTongBoundaryDelay,
  getRotationIntervalMs,
  hasDingTongJumpTarget,
  hasDingTongShopNowText,
  hasValidDingTongJumpTarget,
  resolveDingTong,
} from './ding-tong';

const MAX_TIMEOUT_MS = 2_147_000_000;

function DingTongContainer({
  children,
  className,
  isClickable,
  onClick,
  style,
  domSpm,
}: {
  children: ReactNode;
  className: string;
  isClickable: boolean;
  onClick: () => void;
  style?: CSSProperties;
  /** Data SPM DOM 打标属性：仅可点击分支的真实触发器（button）需要。 */
  domSpm?: { 'data-spm': string };
}) {
  if (isClickable) {
    return (
      <button
        type="button"
        className={`border-0 p-0 ${className}`}
        style={style}
        onClick={onClick}
        {...domSpm}
      >
        {children}
      </button>
    );
  }

  return (
    <div className={className} style={style}>
      {children}
    </div>
  );
}

function ScrollingText({ text, className }: { text: string; className: string }) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [scrollDistance, setScrollDistance] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    const content = textRef.current;
    if (!container || !content) return;

    const measure = () => {
      setScrollDistance(Math.max(0, content.scrollWidth - container.clientWidth));
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);
    resizeObserver.observe(content);
    return () => resizeObserver.disconnect();
  }, [text]);

  const animationDuration = Math.max(4, scrollDistance / 30);
  const marqueeStyle = {
    '--ding-tong-scroll-distance': `${-scrollDistance}px`,
    '--ding-tong-scroll-duration': `${animationDuration}s`,
  } as CSSProperties;

  return (
    <span ref={containerRef} className={`${className} block overflow-hidden whitespace-nowrap`}>
      <span
        ref={textRef}
        className={`inline-block whitespace-nowrap ${scrollDistance > 0 ? styles.marqueeText : ''}`}
        style={marqueeStyle}
      >
        {text}
      </span>
    </span>
  );
}

const DingTong = (props: HeaderProps) => {
  const { dtInfo } = props;
  const { pathname } = useRouteMetadata();
  const isShowDtBanner = useStore((state) =>
    pathname === '/' ? !state.isHomeHeaderCollapsed : state.isShowDtBanner,
  );
  const responseClockRef = useRef({ config: dtInfo, receivedAt: Date.now() });
  if (responseClockRef.current.config !== dtInfo) {
    responseClockRef.current = { config: dtInfo, receivedAt: Date.now() };
  }

  const [now, setNow] = useState(() => Date.now());
  const [activeIndex, setActiveIndex] = useState(0);
  const { trackEvent } = useReporting();
  // 全局组件 B 位固定 global_pc/global_wap，与所在页面无关。
  const spmPage = useGlobalSpmTerminal(props.deviceType);
  // Data SPM DOM 打标：与 handleClick 上报的 open_promotion 坑位同源同值（可点击分支才打标）。
  const openPromotionDomSpm = useSpmDomAttr(
    getSpmPositionCode(spmPage, 'site_notice', 'open_promotion'),
  );
  const toJumpTarget = useJump();
  const receivedAt = responseClockRef.current.receivedAt;

  const resolved = useMemo(
    () => resolveDingTong(dtInfo, pathname, now, receivedAt),
    [dtInfo, now, pathname, receivedAt],
  );
  const itemsKey = resolved?.items
    .map((item, index) => `${item.id ?? index}:${item.deadline_time_TUC_5_unix ?? ''}`)
    .join('|');
  const activeItemsKeyRef = useRef(itemsKey);
  const displayIndex = activeItemsKeyRef.current === itemsKey ? activeIndex : 0;
  activeItemsKeyRef.current = itemsKey;
  const currentItem = resolved?.items[displayIndex % resolved.items.length];
  const itemsLength = resolved?.items.length || 0;
  const rotationIntervalMs = resolved ? getRotationIntervalMs(resolved.moduleSetting) : 0;
  const countdownDisplay = useMemo(
    () => (currentItem ? getCountdownDisplay(currentItem, now, receivedAt) : null),
    [currentItem, now, receivedAt],
  );

  useEffect(() => {
    setActiveIndex(0);
    setNow(Date.now());
  }, [dtInfo, itemsKey, pathname, resolved?.page]);

  useEffect(() => {
    if (itemsLength <= 1) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      setActiveIndex((currentIndex) => (currentIndex + 1) % itemsLength);
    }, rotationIntervalMs);
    return () => window.clearInterval(timer);
  }, [itemsKey, itemsLength, rotationIntervalMs]);

  useEffect(() => {
    if (!dtInfo) return;
    const delay = getNextDingTongBoundaryDelay(dtInfo, currentItem, now, receivedAt);
    if (delay === null) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(MAX_TIMEOUT_MS, Math.max(50, delay + 25)),
    );
    return () => window.clearTimeout(timer);
  }, [currentItem, dtInfo, now, receivedAt]);

  const handleCountDownEnd = useCallback(() => setNow(Date.now()), []);

  if (!resolved || !currentItem) return null;

  const backgroundColor = getDingTongBackgroundColor();
  const showShopNow = hasDingTongShopNowText(currentItem);
  const isClickable = hasDingTongJumpTarget(currentItem);
  const jumpContent = getDingTongJumpContent(currentItem);
  const trackingJumpType = getDingTongTrackingJumpType(currentItem);

  const handleClick = () => {
    if (!isClickable) return;

    if (!hasValidDingTongJumpTarget(currentItem)) {
      logger.error('[DingTong] invalid jump target', {
        itemId: currentItem.id,
        page: resolved.page,
        jumpType: currentItem.jump_type,
        jumpTarget: currentItem.Jump_target,
      });
      return;
    }

    setCampaignEntrancePageScene('顶通', String(currentItem.Jump_target || ''));
    const openPromotionSpmCnt = getSpmPositionCode(spmPage, 'site_notice', 'open_promotion');
    const properties = {
      page_name: getRouteCnName(location.pathname),
      jump_type: trackingJumpType,
      jump_content: jumpContent,
    };
    trackEvent(
      {
        eventName: 'DingTong_Click',
        properties: openPromotionSpmCnt ? withSpmCnt(properties, openPromotionSpmCnt) : properties,
      },
      [ProviderType.SENSORS],
    );
    try {
      toJumpTarget(currentItem as JumpTarget);
    } catch (error) {
      logger.error('[DingTong] jump failed', error, {
        itemId: currentItem.id,
        page: resolved.page,
        jumpType: currentItem.jump_type,
        jumpTarget: currentItem.Jump_target,
      });
    }
  };

  const handleExposure = (inView: boolean) => {
    if (!inView) return;
    const spmCnt = getSpmPositionCode(spmPage, 'site_notice', 'code');
    trackEvent(
      {
        eventName: 'DingTong_Exp',
        properties: {
          page_name: getRouteCnName(location.pathname),
          jump_type: trackingJumpType,
          jump_content: jumpContent,
          ...(spmCnt ? { spm_cnt: spmCnt } : {}),
        },
      },
      [ProviderType.SENSORS],
    );
  };

  return (
    <InView
      // 路由切换直接采用目标页高度，避免沿用上一页横幅的展开/收起动画。
      key={`${pathname}-${resolved.page}-${currentItem.id ?? activeIndex}-${currentItem.dt_text ?? ''}`}
      triggerOnce
      onChange={handleExposure}
    >
      <WapOnly>
        <DingTongContainer
          className={`block w-full overflow-hidden transition-all duration-300 ${isClickable ? 'cursor-pointer' : ''} ${!isShowDtBanner ? 'h-[0px]' : 'h-[10.667vw]'}`}
          style={{ backgroundColor }}
          isClickable={isClickable}
          onClick={handleClick}
          domSpm={hasValidDingTongJumpTarget(currentItem) ? openPromotionDomSpm : undefined}
        >
          <div
            className="DingTong w-full m-[0_auto] relative overflow-hidden flex items-center justify-center h-[9.600vw]"
            style={{ backgroundColor }}
          >
            <ScrollingText
              text={currentItem.dt_text || ''}
              className="text-[3.2vw] text-primary-white max-w-[70vw]"
            />
            {showShopNow ? (
              <span className="text-[3.2vw] text-primary-white underline ml-[5.333vw] shrink-0">
                {currentItem.shop_now_text}
              </span>
            ) : null}
            {countdownDisplay ? (
              <div className="flex ml-[5.333vw] shrink-0">
                <ClientOnly>
                  <WapTopBarCountDown
                    time={countdownDisplay.remainingSeconds}
                    textColor={countdownDisplay.textColor}
                    numberColor={countdownDisplay.numberColor}
                    onCountDownEnd={handleCountDownEnd}
                  />
                </ClientOnly>
              </div>
            ) : null}
          </div>
        </DingTongContainer>
      </WapOnly>
      <WebOnly>
        <div style={{ backgroundColor }}>
          <DingTongContainer
            className={`flex justify-center items-center ${isClickable ? 'cursor-pointer' : ''} relative w-full overflow-hidden transition-all duration-300 ${!isShowDtBanner ? 'h-[0px]' : 'h-[34px]'}`}
            style={{ backgroundColor }}
            isClickable={isClickable}
            onClick={handleClick}
            domSpm={hasValidDingTongJumpTarget(currentItem) ? openPromotionDomSpm : undefined}
          >
            <ScrollingText
              text={currentItem.dt_text || ''}
              className="text-primary-white text-12 max-w-[60vw]"
            />
            {showShopNow ? (
              <span className="text-primary-white text-12 ml-[72px] underline shrink-0">
                {currentItem.shop_now_text}
              </span>
            ) : null}
            {countdownDisplay ? (
              <ClientOnly>
                <PcTopBarCountDown
                  time={countdownDisplay.remainingSeconds}
                  textColor={countdownDisplay.textColor}
                  numberColor={countdownDisplay.numberColor}
                  onCountDownEnd={handleCountDownEnd}
                />
              </ClientOnly>
            ) : null}
          </DingTongContainer>
        </div>
      </WebOnly>
    </InView>
  );
};

export default DingTong;
